import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../utils/config";

import {
    FiBriefcase, FiMapPin, FiClock, FiCheck, FiAward,
    FiStar, FiUser, FiArrowRight, FiAlertTriangle,
    FiList, FiCode, FiArrowLeft, FiUsers, FiHeart, FiInfo, FiCalendar
} from "react-icons/fi";
import { FaRupeeSign } from "react-icons/fa";

const API_BASE = API_BASE_URL;

function JobDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [hasApplied, setHasApplied] = useState(false);
    const context = useOutletContext() || {};
    const { savedJobs = [], setSavedJobs = () => { }, toggleSaveJob } = context;
    const isSaved = savedJobs.some(sj => (sj._id || sj) === id);
    const [showCompanyDetails, setShowCompanyDetails] = useState(false);

    const isExpired = job && job.expiryDate && new Date(job.expiryDate) < new Date(new Date().setHours(0, 0, 0, 0));

    useEffect(() => {
        const fetchJobDetails = async () => {
            try {
                const token = localStorage.getItem("candidateToken");
                if (token) {
                    try {
                        const appRes = await axios.get(`${API_BASE_URL}/candidate/applications`, {
                            headers: { Authorization: `Bearer ${token}` }
                        });
                        const alreadyApplied = appRes.data.some(app => {
                            const appId = app.jobId?._id || app.jobId;
                            return appId && appId.toString() === id.toString();
                        });
                        setHasApplied(alreadyApplied);
                    } catch (e) { /* silent */ }
                }
                const response = await axios.get(`${API_BASE_URL}/jobs/view/${id}`);
                if (response.data.success) {
                    setJob(response.data.jobPost);
                } else {
                    setError("Job post not found.");
                }
            } catch (err) {
                setError(err.response?.data?.message || "Failed to load job details.");
            } finally {
                setLoading(false);
            }
        };
        fetchJobDetails();
    }, [id]);

    const handleToggleSave = () => {
        if (toggleSaveJob) {
            toggleSaveJob(id);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ background: "linear-gradient(135deg,#0f172a 0%,#1e1b4b 50%,#0f172a 100%)" }}>
                <div className="flex flex-col items-center gap-6">
                    <div className="relative w-16 h-16">
                        <div className="absolute inset-0 rounded-2xl border-4 border-violet-900 animate-pulse" />
                        <div className="absolute inset-0 rounded-2xl border-4 border-t-violet-400 border-transparent animate-spin" />
                    </div>
                    <p style={{ color: "#a78bfa", fontSize: 11, fontWeight: 800, letterSpacing: "0.35em", textTransform: "uppercase" }}>
                        Loading Job Details...
                    </p>
                </div>
            </div>
        );
    }

    if (error || !job) {
        return (
            <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "#f8fafc" }}>
                <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-12 text-center border border-red-50">
                    <div className="w-20 h-20 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                        <FiAlertTriangle color="#ef4444" size={32} />
                    </div>
                    <h2 className="text-2xl font-black text-gray-900 mb-3">Job Not Found</h2>
                    <p className="text-gray-500 text-sm mb-8">{error || "This post may have been archived."}</p>
                    <button onClick={() => navigate(-1)}
                        style={{ background: "linear-gradient(135deg,#6366f1,#8b5cf6)", color: "#fff" }}
                        className="px-8 py-3 rounded-xl font-bold text-sm hover:opacity-90 transition-all">
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    const hasAssessment = (job.assessmentIds && job.assessmentIds.length > 0) || job.assessmentId;
    const postedDate = job.createdAt && !isNaN(new Date(job.createdAt).getTime())
        ? new Date(job.createdAt).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })
        : "Recently";

    const skills = job.skills
        ? job.skills.split(",").map(s => s.trim()).filter(Boolean)
        : [];

    return (
        <div className="font-sans text-slate-800" style={{ minHeight: "100vh", background: "#f1f5f9" }}>

            {/* ── HERO ─────────────────────────────────────────────── */}
            <div style={{
                background: "linear-gradient(135deg, #0f172a 0%, #1e1b4b 55%, #312e81 100%)",
                position: "relative",
                overflow: "hidden",
                paddingTop: 64,
                paddingBottom: 80,
            }}>
                {/* decorative blobs */}
                <div style={{ position: "absolute", top: -80, right: -80, width: 400, height: 400, borderRadius: "50%", background: "rgba(139,92,246,0.12)", filter: "blur(60px)", pointerEvents: "none" }} />
                <div style={{ position: "absolute", bottom: -60, left: -60, width: 300, height: 300, borderRadius: "50%", background: "rgba(99,102,241,0.1)", filter: "blur(50px)", pointerEvents: "none" }} />

                <div className="max-w-6xl mx-auto px-4 relative z-10">

                    {/* back link */}
                    <button onClick={() => navigate(-1)}
                        style={{ color: "#a5b4fc", fontSize: 12, fontWeight: 700, letterSpacing: "0.05em", display: "flex", alignItems: "center", gap: 6, marginBottom: 28, background: "none", border: "none", cursor: "pointer" }}>
                        <FiArrowLeft size={16} /> Back to Jobs
                    </button>

                    {/* badges */}
                    <div className="flex flex-wrap gap-2 mb-5">
                        <span style={{ background: "rgba(99,102,241,0.25)", color: "#c4b5fd", border: "1px solid rgba(139,92,246,0.3)", borderRadius: 8, padding: "4px 14px", fontSize: 10, fontWeight: 800, letterSpacing: "0.15em", textTransform: "uppercase" }}>
                            Career Opportunity
                        </span>
                        <span style={{ background: "rgba(16,185,129,0.2)", color: "#6ee7b7", border: "1px solid rgba(16,185,129,0.3)", borderRadius: 8, padding: "4px 14px", fontSize: 10, fontWeight: 800, letterSpacing: "0.15em", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 5 }}>
                            <FiCheck size={12} color="#6ee7b7" />
                            Now Hiring
                        </span>
                        {hasAssessment && (
                            <span style={{ background: "rgba(251,191,36,0.2)", color: "#fde68a", border: "1px solid rgba(251,191,36,0.3)", borderRadius: 8, padding: "4px 14px", fontSize: 10, fontWeight: 800, letterSpacing: "0.15em", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 5 }}>
                                <FiStar size={12} color="#fde68a" />
                                Assessment Included
                            </span>
                        )}
                        {job.expiryDate && (
                            <span style={{ background: isExpired ? "rgba(244,63,94,0.2)" : "rgba(34,197,94,0.2)", color: isExpired ? "#fca5a5" : "#86efac", border: isExpired ? "1px solid rgba(244,63,94,0.3)" : "1px solid rgba(34,197,94,0.3)", borderRadius: 8, padding: "4px 14px", fontSize: 10, fontWeight: 800, letterSpacing: "0.15em", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 5 }}>
                                <FiCalendar size={12} color={isExpired ? "#fca5a5" : "#86efac"} />
                                {isExpired ? "Job Expired" : `Deadline: ${new Date(job.expiryDate).toLocaleDateString()}`}
                            </span>
                        )}
                    </div>

                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
                        <h1 style={{ color: "#fff", fontSize: "clamp(24px,4vw,40px)", fontWeight: 900, letterSpacing: "-0.02em", lineHeight: 1.2, maxWidth: 800 }}>
                            {job.role}
                        </h1>
                        <div className="flex flex-wrap items-center gap-4">
                            {!hasApplied && !isExpired && (
                                <button
                                    onClick={() => navigate(`/applying-job/${id}`)}
                                    style={{
                                        background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                                        color: "#fff",
                                        border: "none",
                                        borderRadius: 14,
                                        padding: "12px 28px",
                                        fontSize: 14,
                                        fontWeight: 900,
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 10,
                                        boxShadow: "0 4px 20px rgba(99,102,241,0.35)",
                                        whiteSpace: "nowrap",
                                        height: "fit-content",
                                        transition: "all 0.25s",
                                    }}
                                    onMouseOver={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(99,102,241,0.45)"; }}
                                    onMouseOut={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(99,102,241,0.35)"; }}>
                                    Apply Now
                                    <FiArrowRight size={16} />
                                </button>
                            )}

                            {isExpired && !hasApplied && (
                                <div style={{ background: "rgba(244,63,94,0.15)", border: "1px solid rgba(244,63,94,0.3)", borderRadius: 14, padding: "12px 24px", display: "flex", alignItems: "center", gap: 8, height: "fit-content" }}>
                                    <FiAlertTriangle size={18} color="#fca5a5" />
                                    <span style={{ color: "#fca5a5", fontSize: 14, fontWeight: 900 }}>This job has expired</span>
                                </div>
                            )}

                            <button
                                onClick={handleToggleSave}
                                style={{
                                    background: isSaved ? "rgba(239, 68, 68, 0.1)" : "rgba(255, 255, 255, 0.05)",
                                    color: isSaved ? "#ef4444" : "#fff",
                                    border: isSaved ? "1px solid rgba(239, 68, 68, 0.3)" : "1px solid rgba(255, 255, 255, 0.2)",
                                    borderRadius: 14,
                                    padding: "12px 20px",
                                    fontSize: 14,
                                    fontWeight: 900,
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    transition: "all 0.25s",
                                }}
                            >
                                <FiHeart size={18} fill={isSaved ? "#ef4444" : "none"} />
                                {isSaved ? "Saved" : "Save"}
                            </button>

                            <button
                                onClick={() => setShowCompanyDetails(!showCompanyDetails)}
                                style={{
                                    background: showCompanyDetails ? "rgba(99, 102, 241, 0.15)" : "rgba(255, 255, 255, 0.05)",
                                    color: "#fff",
                                    border: showCompanyDetails ? "1px solid rgba(99, 102, 241, 0.4)" : "1px solid rgba(255, 255, 255, 0.2)",
                                    borderRadius: 14,
                                    padding: "12px 24px",
                                    fontSize: 14,
                                    fontWeight: 900,
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    transition: "all 0.25s",
                                }}
                            >
                                <FiInfo size={18} className={showCompanyDetails ? "text-indigo-400" : ""} />
                                About Company
                            </button>

                            {hasApplied && (
                                <div style={{ background: "rgba(34,197,94,0.15)", border: "1px solid rgba(34,197,94,0.3)", borderRadius: 12, padding: "10px 20px", display: "flex", alignItems: "center", gap: 8, height: "fit-content" }}>
                                    <FiCheck size={16} color="#4ade80" />
                                    <span style={{ color: "#4ade80", fontSize: 13, fontWeight: 700 }}>Application Submitted</span>
                                </div>
                            )}

                            {localStorage.getItem("candidateToken") ? (
                                <button
                                    onClick={() => navigate("/candidate-dashboard")}
                                    style={{
                                        background: "rgba(255, 255, 255, 0.05)",
                                        color: "#fff",
                                        border: "1px solid rgba(255, 255, 255, 0.2)",
                                        borderRadius: 14,
                                        padding: "12px 28px",
                                        fontSize: 14,
                                        fontWeight: 700,
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 8,
                                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                        backdropFilter: "blur(12px)",
                                        WebkitBackdropFilter: "blur(12px)",
                                        whiteSpace: "nowrap",
                                    }}
                                    onMouseOver={e => {
                                        e.currentTarget.style.background = "rgba(255, 255, 255, 0.12)";
                                        e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.45)";
                                        e.currentTarget.style.transform = "translateY(-1px)";
                                    }}
                                    onMouseOut={e => {
                                        e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)";
                                        e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.2)";
                                        e.currentTarget.style.transform = "translateY(0)";
                                    }}>
                                    <FiStar size={16} className="text-indigo-400" />
                                    Dashboard
                                </button>
                            ) : (
                                <button
                                    onClick={() => navigate("/candidate-login")}
                                    style={{
                                        background: "rgba(255, 255, 255, 0.05)",
                                        color: "#fff",
                                        border: "1px solid rgba(255, 255, 255, 0.2)",
                                        borderRadius: 14,
                                        padding: "12px 28px",
                                        fontSize: 14,
                                        fontWeight: 700,
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 8,
                                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                        backdropFilter: "blur(12px)",
                                        WebkitBackdropFilter: "blur(12px)",
                                        whiteSpace: "nowrap",
                                    }}
                                    onMouseOver={e => {
                                        e.currentTarget.style.background = "rgba(255, 255, 255, 0.12)";
                                        e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.45)";
                                        e.currentTarget.style.transform = "translateY(-1px)";
                                    }}
                                    onMouseOut={e => {
                                        e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)";
                                        e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.2)";
                                        e.currentTarget.style.transform = "translateY(0)";
                                    }}>
                                    <FiUser size={16} className="text-indigo-400" />
                                    Login
                                </button>
                            )}
                        </div>
                    </div>

                    {/* meta pills */}
                    <div className="flex flex-wrap gap-3">
                        {job.location && (
                            <div style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 12, padding: "8px 16px", display: "flex", alignItems: "center", gap: 8 }}>
                                <FiMapPin size={15} color="#a78bfa" />
                                <span style={{ color: "#e2e8f0", fontSize: 13, fontWeight: 700 }}>{job.location}</span>
                            </div>
                        )}
                        {job.salary && (
                            <div style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 12, padding: "8px 16px", display: "flex", alignItems: "center", gap: 8 }}>
                                <FaRupeeSign size={15} color="#34d399" />
                                <span style={{ color: "#e2e8f0", fontSize: 13, fontWeight: 700 }}>{job.salary}</span>
                            </div>
                        )}
                        {job.experience && (
                            <div style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 12, padding: "8px 16px", display: "flex", alignItems: "center", gap: 8 }}>
                                <FiAward size={15} color="#60a5fa" />
                                <span style={{ color: "#e2e8f0", fontSize: 13, fontWeight: 700 }}>{job.experience} Experience</span>
                            </div>
                        )}
                        {job.department && (
                            <div style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 12, padding: "8px 16px", display: "flex", alignItems: "center", gap: 8 }}>
                                <FiBriefcase size={15} color="#818cf8" />
                                <span style={{ color: "#e2e8f0", fontSize: 13, fontWeight: 700 }}>{job.department}</span>
                            </div>
                        )}
                        {job.vacancies > 0 && (
                            <div style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 12, padding: "8px 16px", display: "flex", alignItems: "center", gap: 8 }}>
                                <FiUsers size={15} color="#fbbf24" />
                                <span style={{ color: "#e2e8f0", fontSize: 13, fontWeight: 700 }}>{job.vacancies} {job.vacancies === 1 ? 'Vacancy' : 'Vacancies'}</span>
                            </div>
                        )}
                        {job.jobType === "short" && job.startTime && job.endTime && (
                            <div style={{ background: "rgba(139,92,246,0.15)", border: "1px solid rgba(139,92,246,0.3)", borderRadius: 12, padding: "8px 16px", display: "flex", alignItems: "center", gap: 8 }}>
                                <FiClock size={15} color="#c4b5fd" />
                                <span style={{ color: "#e2e8f0", fontSize: 13, fontWeight: 700 }}>{job.startTime} - {job.endTime} ({job.totalDuration})</span>
                            </div>
                        )}
                        <div style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 12, padding: "8px 16px", display: "flex", alignItems: "center", gap: 8 }}>
                            <FiClock size={15} color="#f9a8d4" />
                            <span style={{ color: "#94a3b8", fontSize: 13, fontWeight: 700 }}>Posted {postedDate}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── MAIN CONTENT ─────────────────────────────────────── */}
            <div className="max-w-6xl mx-auto px-4" style={{ marginTop: -40, position: "relative", zIndex: 10, paddingBottom: 80 }}>
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start">

                    {/* ── Left Column ── */}
                    <div className="lg:col-span-3 space-y-6">

                        {/* About Company Card (Toggleable) */}
                        {showCompanyDetails && (
                            <div style={{
                                background: "#fff",
                                borderRadius: 20,
                                border: "2px solid #e0e7ff",
                                overflow: "hidden",
                                boxShadow: "0 10px 30px rgba(99,102,241,0.08)",
                                animation: "fadeIn 0.3s ease-out"
                            }}>
                                <div style={{ padding: "20px 32px 16px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 12, background: "linear-gradient(to right, #f8fafc, #fff)" }}>
                                    <div style={{ width: 40, height: 40, background: "linear-gradient(135deg,#4f46e5,#7c3aed)", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                        <FiBriefcase size={18} color="#fff" />
                                    </div>
                                    <div>
                                        <h2 style={{ fontSize: 17, fontWeight: 900, color: "#0f172a", margin: 0 }}>{job.companyName || "Timely Health"}</h2>
                                        <p style={{ fontSize: 10, color: "#6366f1", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", margin: 0 }}>Company Overview</p>
                                    </div>
                                </div>
                                <div style={{ padding: "24px 32px" }}>
                                    <div style={{ color: "#475569", lineHeight: 1.9, fontSize: 15, fontWeight: 500 }}>
                                        {job.aboutCompany || "Timely Health is a leading healthcare recruitment and staffing organization dedicated to connecting talented professionals with top healthcare institutions."}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Description Card */}
                        <div style={{ background: "#fff", borderRadius: 20, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 2px 20px rgba(0,0,0,0.06)" }}>
                            <div style={{ padding: "20px 32px 16px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 12 }}>
                                <div style={{ width: 40, height: 40, background: "linear-gradient(135deg,#6366f1,#8b5cf6)", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                    <FiList size={18} color="#fff" />
                                </div>
                                <div>
                                    <h2 style={{ fontSize: 17, fontWeight: 900, color: "#0f172a", margin: 0 }}>Job Description</h2>
                                    <p style={{ fontSize: 10, color: "#6366f1", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", margin: 0 }}>What you'll be doing</p>
                                </div>
                            </div>
                            <div style={{ padding: "24px 32px" }}>
                                <div style={{ color: "#475569", lineHeight: 1.9, fontSize: 15, fontWeight: 500, whiteSpace: "pre-wrap", borderLeft: "3px solid #e0e7ff", paddingLeft: 20 }}>
                                    {job.description || job.responsibilities || "No description provided."}
                                </div>
                            </div>
                        </div>

                        {/* Skills Card */}
                        {skills.length > 0 && (
                            <div style={{ background: "#fff", borderRadius: 20, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 2px 20px rgba(0,0,0,0.06)" }}>
                                <div style={{ padding: "20px 32px 16px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 12 }}>
                                    <div style={{ width: 40, height: 40, background: "linear-gradient(135deg,#8b5cf6,#d946ef)", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                        <FiCode size={18} color="#fff" />
                                    </div>
                                    <div>
                                        <h2 style={{ fontSize: 17, fontWeight: 900, color: "#0f172a", margin: 0 }}>Required Skills</h2>
                                        <p style={{ fontSize: 10, color: "#9333ea", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", margin: 0 }}>Key qualifications we're looking for</p>
                                    </div>
                                </div>
                                <div style={{ padding: "24px 32px" }}>
                                    <div className="flex flex-wrap gap-3">
                                        {skills.map((skill, idx) => (
                                            <span key={idx} style={{
                                                background: "linear-gradient(135deg,#eef2ff,#f3e8ff)",
                                                color: "#7c3aed",
                                                border: "1px solid #ddd6fe",
                                                borderRadius: 10,
                                                padding: "8px 16px",
                                                fontSize: 12,
                                                fontWeight: 800,
                                                letterSpacing: "0.05em",
                                                cursor: "default",
                                                transition: "all 0.2s",
                                            }}
                                                onMouseOver={e => { e.currentTarget.style.background = "linear-gradient(135deg,#6366f1,#8b5cf6)"; e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "transparent"; }}
                                                onMouseOut={e => { e.currentTarget.style.background = "linear-gradient(135deg,#eef2ff,#f3e8ff)"; e.currentTarget.style.color = "#7c3aed"; e.currentTarget.style.borderColor = "#ddd6fe"; }}>
                                                {skill}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Shift Timings Card */}
                        {job.jobType === "short" && (
                            <div style={{ background: "#fff", borderRadius: 20, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 2px 20px rgba(0,0,0,0.06)" }}>
                                <div style={{ padding: "20px 32px 16px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 12 }}>
                                    <div style={{ width: 40, height: 40, background: "linear-gradient(135deg,#6366f1,#8b5cf6)", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                        <FiClock size={18} color="#fff" />
                                    </div>
                                    <div>
                                        <h2 style={{ fontSize: 17, fontWeight: 900, color: "#0f172a", margin: 0 }}>Job Timings</h2>
                                        <p style={{ fontSize: 10, color: "#6366f1", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", margin: 0 }}>Shift Schedule</p>
                                    </div>
                                </div>
                                <div style={{ padding: "24px 32px" }}>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Start Date</p>
                                            <p className="text-lg font-black text-slate-900">
                                                {job.startDate ? new Date(job.startDate).toLocaleDateString() : 'N/A'}
                                            </p>
                                        </div>
                                        <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">End Date</p>
                                            <p className="text-lg font-black text-slate-900">
                                                {job.endDate ? new Date(job.endDate).toLocaleDateString() : 'N/A'}
                                            </p>
                                        </div>
                                        <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Start Time</p>
                                            <p className="text-lg font-black text-slate-900">{job.startTime}</p>
                                        </div>
                                        <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">End Time</p>
                                            <p className="text-lg font-black text-slate-900">{job.endTime}</p>
                                        </div>
                                        <div className="p-4 rounded-xl bg-indigo-50 border border-indigo-100">
                                            <p className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest mb-1">Total Duration</p>
                                            <p className="text-lg font-black text-indigo-700">{job.totalDuration}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Location + Experience Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {job.location && (
                                <div style={{ background: "#fff", borderRadius: 20, border: "1px solid #e2e8f0", padding: 28, boxShadow: "0 2px 20px rgba(0,0,0,0.06)" }}>
                                    <div style={{ width: 44, height: 44, background: "linear-gradient(135deg,#ef4444,#f97316)", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
                                        <FiMapPin size={20} color="#fff" />
                                    </div>
                                    <h3 style={{ fontSize: 14, fontWeight: 900, color: "#0f172a", marginBottom: 6 }}>Work Location</h3>
                                    <p style={{ fontSize: 15, color: "#475569", fontWeight: 600 }}>{job.location}</p>
                                </div>
                            )}
                            {job.experience && (
                                <div style={{ background: "#fff", borderRadius: 20, border: "1px solid #e2e8f0", padding: 28, boxShadow: "0 2px 20px rgba(0,0,0,0.06)" }}>
                                    <div style={{ width: 44, height: 44, background: "linear-gradient(135deg,#0ea5e9,#6366f1)", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
                                        <FiUser size={20} color="#fff" />
                                    </div>
                                    <h3 style={{ fontSize: 14, fontWeight: 900, color: "#0f172a", marginBottom: 6 }}>Experience Required</h3>
                                    <p style={{ fontSize: 15, color: "#475569", fontWeight: 600 }}>{job.experience}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── Right Column (Sticky Apply) ── */}
                    <div className="lg:col-span-1">
                        <div style={{ position: "sticky", top: 24, display: "flex", flexDirection: "column", gap: 16 }}>

                            {/* Apply Card */}
                            <div style={{ background: "#fff", borderRadius: 24, border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 8px 40px rgba(99,102,241,0.12)" }}>
                                {/* card gradient top bar */}
                                <div style={{ height: 6, background: "linear-gradient(90deg,#6366f1,#8b5cf6,#d946ef)" }} />
                                <div style={{ padding: 28, textAlign: "center" }}>
                                    <div style={{ width: 72, height: 72, background: "linear-gradient(135deg,#eef2ff,#f3e8ff)", borderRadius: 20, border: "1px solid #e0e7ff", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                                        <FiBriefcase size={32} color="#6366f1" />
                                    </div>

                                    <h3 style={{ fontSize: 18, fontWeight: 900, color: "#0f172a", marginBottom: 6 }}>Join Our Team</h3>
                                    <p style={{ fontSize: 12, color: "#64748b", fontWeight: 500, lineHeight: 1.7, marginBottom: 20 }}>
                                        {hasAssessment
                                            ? "Apply and complete a short assessment to start your journey."
                                            : "Submit your application and our team will be in touch soon."}
                                    </p>

                                    {hasAssessment && (
                                        <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, padding: "8px 14px", display: "flex", alignItems: "center", justifyContent: "center", gap: 7, marginBottom: 20 }}>
                                            <FiStar size={13} color="#f59e0b" />
                                            <span style={{ fontSize: 10, fontWeight: 800, color: "#92400e", letterSpacing: "0.1em", textTransform: "uppercase" }}>Assessment Enclosed</span>
                                        </div>
                                    )}

                                    {hasApplied ? (
                                        <div style={{ background: "#f1f5f9", borderRadius: 14, padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                                            <FiCheck size={16} color="#22c55e" />
                                            <span style={{ fontSize: 14, fontWeight: 800, color: "#22c55e" }}>Already Applied</span>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => navigate(`/applying-job/${id}`)}
                                            style={{
                                                width: "100%",
                                                background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                                                color: "#fff",
                                                border: "none",
                                                borderRadius: 14,
                                                padding: "15px 24px",
                                                fontSize: 14,
                                                fontWeight: 900,
                                                cursor: "pointer",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                gap: 10,
                                                transition: "all 0.25s",
                                                boxShadow: "0 4px 20px rgba(99,102,241,0.35)",
                                                letterSpacing: "0.02em",
                                            }}
                                            onMouseOver={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(99,102,241,0.45)"; }}
                                            onMouseOut={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(99,102,241,0.35)"; }}>
                                            Apply Now
                                            <FiArrowRight size={16} color="#fff" />
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Stats Card */}
                            <div style={{ background: "#fff", borderRadius: 20, border: "1px solid #e2e8f0", padding: "20px 24px", display: "flex", justifyContent: "space-around", boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}>
                                <div style={{ textAlign: "center" }}>
                                    <div style={{ fontSize: 22, fontWeight: 900, color: "#6366f1" }}>24h</div>
                                    <div style={{ fontSize: 10, fontWeight: 800, color: "#94a3b8", letterSpacing: "0.1em", textTransform: "uppercase" }}>Response</div>
                                </div>
                                <div style={{ width: 1, background: "#f1f5f9" }} />
                                <div style={{ textAlign: "center" }}>
                                    <div style={{ fontSize: 22, fontWeight: 900, color: "#8b5cf6" }}>Fast</div>
                                    <div style={{ fontSize: 10, fontWeight: 800, color: "#94a3b8", letterSpacing: "0.1em", textTransform: "uppercase" }}>Review</div>
                                </div>
                                <div style={{ width: 1, background: "#f1f5f9" }} />
                                <div style={{ textAlign: "center" }}>
                                    <div style={{ fontSize: 22, fontWeight: 900, color: "#d946ef" }}>100%</div>
                                    <div style={{ fontSize: 10, fontWeight: 800, color: "#94a3b8", letterSpacing: "0.1em", textTransform: "uppercase" }}>Free</div>
                                </div>
                            </div>

                            {/* Company note */}
                            <div style={{ textAlign: "center", padding: "8px 0" }}>
                                <span style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.2em", textTransform: "uppercase" }}>
                                    Timely Health Recruitment
                                </span>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}

export default JobDetails;